import React from "react";
import PropTypes from "prop-types";
// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import { BASE_URL } from "lib/config";
import httpr from "lib/httpRequest";
import ReactLoading from "react-loading";

const styles = (theme) => ({
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  formControl: {
    margin: theme.spacing.unit,
    width: "99%",
  },
});

class LicenseActivation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      token: null,
      tokenValid: false,
      tokenValidated: false,
      tokenError: null,
      locationInfo: null,
      licenseKey: "",
      licenseKeyError: false,
      isSubmitting: false,
      activationSuccess: false,
      activationError: null,
      subscriptionType: null,
      expirationPreview: null,
      activatedLicense: null,
    };
  }

  componentDidMount() {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    
    // Get token from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    
    if (token) {
      this.setState({ token });
      this.validateToken(token);
    } else {
      this.setState({
        tokenValidated: true,
        tokenValid: false,
        tokenError: "No token provided in URL",
      });
    }
  }

  validateToken = (token) => {
    const fetch = httpr(null); // No auth token needed for public endpoint

    console.log("Validating token:", token);
    console.log("API URL:", `${BASE_URL}/api/license/token/${token}`);

    fetch
      .get(`${BASE_URL}/api/license/token/${token}`)
      .then((response) => {
        console.log("Token validation response:", response);
        console.log("Response data:", response.data);
        
        if (response.data && response.data.success && response.data.isValid) {
          this.setState({
            tokenValid: true,
            tokenValidated: true,
            locationInfo: {
              locationId: response.data.locationId,
              locationName: response.data.locationName,
              organizationName: response.data.organizationName,
              expiresAt: response.data.expiresAt,
            },
          });
        } else {
          // Handle case where response is successful but token is invalid
          const errorMessage = (response.data && response.data.error) || 
                              (response.data && response.data.status) || 
                              "Token is invalid or expired";
          this.setState({
            tokenValid: false,
            tokenValidated: true,
            tokenError: errorMessage,
          });
        }
      })
      .catch((error) => {
        console.log("Error validating token:", error);
        console.log("Error response:", error.response);
        console.log("Error response data:", error.response && error.response.data);
        
        // Handle 400 errors (invalid token) - these still have response data
        if (error.response && error.response.data) {
          const errorMessage = error.response.data.error || 
                              error.response.data.status || 
                              "Token is invalid or expired";
          this.setState({
            tokenValid: false,
            tokenValidated: true,
            tokenError: errorMessage,
          });
        } else {
          // Handle network errors or other issues
          this.setState({
            tokenValid: false,
            tokenValidated: true,
            tokenError: error.message || "Failed to validate token. Please check your URL and try again.",
          });
        }
      });
  };

  parseLicenseKey = (key) => {
    const trimmed = key.trim();
    if (!trimmed) {
      return null;
    }

    // Check for legacy format (32-char hex without prefix)
    const legacyRegex = /^[a-f0-9]{32}$/i;
    if (legacyRegex.test(trimmed)) {
      return {
        type: "M12", // Default to 12-month for legacy keys
        isLegacy: true,
        fullKey: trimmed,
      };
    }

    // Check for Option 4 format: [TYPE]-[32-char-hex] or CUST-[YYYYMMDD]-[32-char-hex]
    const parts = trimmed.split("-");
    
    if (parts.length < 2) {
      return null;
    }

    const type = parts[0].toUpperCase();
    
    if (type === "CUST" && parts.length === 3) {
      // CUST-[YYYYMMDD]-[UUID]
      const dateStr = parts[1];
      if (!/^\d{8}$/.test(dateStr)) {
        return null;
      }
      return {
        type: "CUST",
        expirationDate: dateStr,
        isLegacy: false,
        fullKey: trimmed,
      };
    } else if (parts.length === 2) {
      // [TYPE]-[UUID]
      const uuid = parts[1];
      if (!/^[a-f0-9]{32}$/i.test(uuid)) {
        return null;
      }
      return {
        type: type,
        isLegacy: false,
        fullKey: trimmed,
      };
    }

    return null;
  };

  calculateExpirationPreview = (parsedKey) => {
    if (!parsedKey) {
      return null;
    }

    const now = new Date();
    const expiration = new Date(now);

    if (parsedKey.type === "CUST" && parsedKey.expirationDate) {
      // Parse YYYYMMDD format
      const dateStr = parsedKey.expirationDate;
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return new Date(`${year}-${month}-${day}T23:59:59Z`);
    }

    switch (parsedKey.type) {
      case "T30":
        expiration.setDate(expiration.getDate() + 30);
        break;
      case "T90":
        expiration.setDate(expiration.getDate() + 90);
        break;
      case "M03":
        expiration.setMonth(expiration.getMonth() + 3);
        break;
      case "M06":
        expiration.setMonth(expiration.getMonth() + 6);
        break;
      case "M12":
        expiration.setFullYear(expiration.getFullYear() + 1);
        break;
      case "M24":
        expiration.setFullYear(expiration.getFullYear() + 2);
        break;
      default:
        return null;
    }

    return expiration;
  };

  getSubscriptionTypeLabel = (type) => {
    const labels = {
      T30: "30-Day Trial",
      T90: "90-Day Trial",
      M03: "3-Month Subscription",
      M06: "6-Month Subscription",
      M12: "12-Month Subscription",
      M24: "24-Month Subscription",
      CUST: "Custom Expiration",
    };
    return labels[type] || type;
  };

  handleLicenseKeyChange = (event) => {
    const licenseKey = event.target.value;
    const parsedKey = this.parseLicenseKey(licenseKey);
    const expirationPreview = parsedKey ? this.calculateExpirationPreview(parsedKey) : null;

    this.setState({
      licenseKey: licenseKey,
      licenseKeyError: false,
      subscriptionType: parsedKey ? parsedKey.type : null,
      expirationPreview: expirationPreview,
    });
  };

  handleActivate = () => {
    const { token, licenseKey } = this.state;

    // Validate license key format (Option 4: [TYPE]-[32-char-hex] or CUST-[YYYYMMDD]-[32-char-hex])
    // Also support legacy format: [32-char-hex]
    const trimmedKey = licenseKey.trim();

    if (!trimmedKey) {
      this.setState({ licenseKeyError: true });
      return;
    }

    // Parse the key to validate format
    const parsedKey = this.parseLicenseKey(trimmedKey);
    
    if (!parsedKey) {
      this.setState({
        licenseKeyError: true,
        activationError: "Invalid license key format. Expected format: [TYPE]-[32-char-hex] (e.g., M12-abc123...) or CUST-[YYYYMMDD]-[32-char-hex]",
      });
      return;
    }

    // Additional validation for Option 4 format
    const option4Regex = /^([A-Z0-9]{2,4}|CUST-\d{8})-[a-f0-9]{32}$/i;
    const legacyRegex = /^[a-f0-9]{32}$/i;
    
    if (!option4Regex.test(trimmedKey) && !legacyRegex.test(trimmedKey)) {
      this.setState({
        licenseKeyError: true,
        activationError: "Invalid license key format. Expected format: [TYPE]-[32-char-hex] (e.g., M12-abc123...) or CUST-[YYYYMMDD]-[32-char-hex]",
      });
      return;
    }

    this.setState({ isSubmitting: true, activationError: null });

    const fetch = httpr(null); // No auth token needed for public endpoint

    fetch
      .post(`${BASE_URL}/api/license/activate`, {
        token: token,
        licenseKey: trimmedKey,
      })
      .then((response) => {
        if (response.data && response.data.success) {
          // Check if user is authenticated (has token in localStorage)
          const token = localStorage.getItem("token");
          const isAuthenticated = token && token.trim() !== "";

          this.setState({
            isSubmitting: false,
            activationSuccess: true,
            activatedLicense: response.data.license || null,
          });

          // If authenticated, redirect to dashboard with refresh flag
          if (isAuthenticated) {
            // Use setTimeout to allow state update before redirect
            setTimeout(() => {
              window.location.href = "/dashboard?licenseActivated=true";
            }, 500);
          }
          // If not authenticated, just show success message (no redirect)
        } else {
          this.setState({
            isSubmitting: false,
            activationError: (response.data && response.data.error) || "Failed to activate license",
          });
        }
      })
      .catch((error) => {
        console.log("Error activating license:", error);
        this.setState({
          isSubmitting: false,
          activationError: (error.response && error.response.data && error.response.data.error) || "Failed to activate license. Please try again.",
        });
      });
  };

  render() {
    const { classes } = this.props;
    const {
      tokenValidated,
      tokenValid,
      tokenError,
      locationInfo,
      licenseKey,
      licenseKeyError,
      isSubmitting,
      activationSuccess,
      activationError,
      subscriptionType,
      expirationPreview,
      activatedLicense,
    } = this.state;

    // Loading state - validating token
    if (!tokenValidated) {
      return (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="info">
                <h4 className={classes.cardTitleWhite}>License Activation</h4>
                <p className={classes.cardCategoryWhite}>
                  Validating your license update token...
                </p>
              </CardHeader>
              <CardBody>
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <ReactLoading
                    type="spin"
                    color="#000000"
                    style={{
                      width: "20%",
                      margin: "auto",
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      );
    }

    // Error state - token invalid/expired
    if (!tokenValid) {
      return (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="danger">
                <h4 className={classes.cardTitleWhite}>License Activation</h4>
                <p className={classes.cardCategoryWhite}>
                  Unable to activate license
                </p>
              </CardHeader>
              <CardBody>
                <div style={{ padding: "20px" }}>
                  <h4 style={{ color: "#f44336", marginBottom: "15px" }}>
                    Token Error
                  </h4>
                  <p style={{ marginBottom: "20px" }}>{tokenError}</p>
                  <div style={{ 
                    padding: "15px", 
                    backgroundColor: "#f5f5f5", 
                    borderRadius: "4px",
                    border: "1px solid #ddd"
                  }}>
                    <p style={{ margin: "0 0 10px 0", fontWeight: "bold" }}>
                      Please contact SCSC Support for assistance:
                    </p>
                    <p style={{ margin: "0" }}>
                      Email: brittanydeventer@socalsafe.com
                    </p>
                    <p style={{ margin: "5px 0 0 0" }}>
                      Phone: 1-(800)334-6060
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      );
    }

    // Success state - license activated
    if (activationSuccess) {
      return (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="success">
                <h4 className={classes.cardTitleWhite}>License Activated</h4>
                <p className={classes.cardCategoryWhite}>
                  Your license has been successfully updated
                </p>
              </CardHeader>
              <CardBody>
                <div style={{ padding: "20px" }}>
                  <h4 style={{ color: "#4caf50", marginBottom: "15px" }}>
                    ✓ License Activation Successful
                  </h4>
                  <p style={{ marginBottom: "10px" }}>
                    <strong>Location:</strong> {locationInfo && locationInfo.locationName}
                  </p>
                  {locationInfo && locationInfo.organizationName && (
                    <p style={{ marginBottom: "10px" }}>
                      <strong>Organization:</strong> {locationInfo.organizationName}
                    </p>
                  )}
                  {activatedLicense && activatedLicense.subscriptionType && (
                    <p style={{ marginBottom: "10px" }}>
                      <strong>Subscription Type:</strong> {this.getSubscriptionTypeLabel(activatedLicense.subscriptionType)}
                    </p>
                  )}
                  {activatedLicense && activatedLicense.expirationDate && (
                    <p style={{ marginBottom: "10px" }}>
                      <strong>Expiration Date:</strong> {new Date(activatedLicense.expirationDate).toLocaleDateString()} ({new Date(activatedLicense.expirationDate).toLocaleTimeString()})
                    </p>
                  )}
                  {activatedLicense && activatedLicense.daysUntilExpiration !== undefined && (
                    <p style={{ marginBottom: "20px" }}>
                      <strong>Days Until Expiration:</strong> {activatedLicense.daysUntilExpiration} days
                    </p>
                  )}
                  <p style={{ marginBottom: "20px" }}>
                    Your license has been successfully updated. You can now use all features for this location.
                  </p>
                  
                  {/* Show "Go to Login" button if user is not authenticated */}
                  {!localStorage.getItem("token") && (
                    <div style={{ marginBottom: "20px" }}>
                      <Button
                        color="info"
                        onClick={() => {
                          window.location.href = "/login";
                        }}
                      >
                        Go to Login
                      </Button>
                    </div>
                  )}
                  
                  <div style={{ 
                    padding: "15px", 
                    backgroundColor: "#f5f5f5", 
                    borderRadius: "4px",
                    border: "1px solid #ddd"
                  }}>
                    <p style={{ margin: "0 0 10px 0", fontWeight: "bold" }}>
                      Need assistance?
                    </p>
                    <p style={{ margin: "0" }}>
                      Email: brittanydeventer@socalsafe.com
                    </p>
                    <p style={{ margin: "5px 0 0 0" }}>
                      Phone: 1-(800)334-6060
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      );
    }

    // Form state - token valid, show activation form
    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="success">
              <h4 className={classes.cardTitleWhite}>License Activation</h4>
              <p className={classes.cardCategoryWhite}>
                Enter your license key to activate
              </p>
            </CardHeader>
            <CardBody>
              <div style={{ padding: "20px" }}>
                {locationInfo && (
                  <div style={{ marginBottom: "20px" }}>
                    <p style={{ marginBottom: "5px" }}>
                      <strong>Location:</strong> {locationInfo.locationName}
                    </p>
                    {locationInfo.organizationName && (
                      <p style={{ marginBottom: "15px" }}>
                        <strong>Organization:</strong> {locationInfo.organizationName}
                      </p>
                    )}
                    {locationInfo.expiresAt && (
                      <p style={{ marginBottom: "15px", fontSize: "12px", color: "#666" }}>
                        Token expires: {new Date(locationInfo.expiresAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                {activationError && (
                  <div style={{
                    padding: "10px",
                    marginBottom: "20px",
                    backgroundColor: "#ffebee",
                    border: "1px solid #f44336",
                    borderRadius: "4px",
                    color: "#c62828"
                  }}>
                    {activationError}
                  </div>
                )}

                <div style={{ marginBottom: "20px" }}>
                  <CustomInput
                    labelText="License Key (Required)"
                    id="licenseKey"
                    name="licenseKey"
                    value={licenseKey}
                    error={licenseKeyError}
                    onChange={this.handleLicenseKeyChange}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      placeholder: "Enter license key (e.g., M12-abc123def456...)",
                      style: {
                        fontFamily: "monospace",
                        fontSize: "14px"
                      }
                    }}
                  />
                  {licenseKeyError && (
                    <p style={{ color: "#f44336", fontSize: "12px", marginTop: "5px" }}>
                      {activationError || "Please enter a valid license key in the format: [TYPE]-[32-char-hex] (e.g., M12-abc123...)"}
                    </p>
                  )}
                  
                  {/* Subscription Type Preview */}
                  {subscriptionType && !licenseKeyError && (
                    <div style={{
                      marginTop: "10px",
                      padding: "10px",
                      backgroundColor: "#e8f5e9",
                      border: "1px solid #4caf50",
                      borderRadius: "4px",
                      fontSize: "13px"
                    }}>
                      <p style={{ margin: "0 0 5px 0", fontWeight: "bold", color: "#2e7d32" }}>
                        Subscription Type: {this.getSubscriptionTypeLabel(subscriptionType)}
                      </p>
                      {expirationPreview && (
                        <p style={{ margin: "0", color: "#2e7d32" }}>
                          Estimated Expiration: {expirationPreview.toLocaleDateString()} ({expirationPreview.toLocaleTimeString()})
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardBody>
            <CardFooter>
              <Button
                color="success"
                onClick={this.handleActivate}
                disabled={isSubmitting || !licenseKey.trim()}
              >
                {isSubmitting ? (
                  <ReactLoading
                    type="spin"
                    color="#FFFFFF"
                    height={20}
                    width={20}
                  />
                ) : (
                  "Activate License"
                )}
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

LicenseActivation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(LicenseActivation);




// WEBPACK FOOTER //
// ./src/views/LicenseActivation/LicenseActivation.jsx